import { EventEmitter, Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import structuredClone from '@ungap/structured-clone';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { TagCompany } from 'src/app/shared/models/company';
import { DateTimezonePipe } from 'src/app/shared/pipes/date-timezone.pipe';
import { BasicService } from 'src/app/shared/services/basic.service';

import { FilterType } from '../shared/enums';
import { ContextualFilter, Filter, TaskList, TaskListConfiguration } from '../shared/models';

@Injectable({
  providedIn: 'root',
})
export class FiltersService {
  updateTplFiltersBS: EventEmitter<any> = new EventEmitter(); // some filters depend on tasks, so we notifity each time task list is loaded
  private _filters: Filter[] = [];
  private _storageKey: string;
  public isFiltersOrSitesSelected = false;

  get filters(): Filter[] {
    return structuredClone(this._filters);
  }

  get storageKey() {
    return this._storageKey;
  }

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private dateTimezonePipe: DateTimezonePipe,
    private storage: Storage
  ) {
    this._storageKey = `inbox-filters-${this.alcmeonContextService.companyId}`;
  }

  buildInitialFilters(): Filter[] {
    this._filters = this.getRoutingTagsForTaskListFilters();
    return this.filters;
  }

  /**
   * This is necessary when changing channel, to avoid mess
   */
  resetfilters() {
    this._filters = [];
    this.storage.remove(this.storageKey);
  }

  /**
   * Update current filters (publication and mention) with new ones (typically coming from getTask).
   * Important to know : When selecting an item in list of publications or mentions, the answer of getTask will return a list of filters
   * containing only one item (the item that the user just selected).
   * But we want to keep displaying the full initial list (hence the complex if).
   *
   * @param newFilters list of filters
   */
  private updateFilters(newFilters: Filter[]) {
    newFilters.forEach(newFilter => {
      const filterToReplaceIndex = this._filters.findIndex(currentFilter => {
        return newFilter.type === currentFilter.type;
      });

      if (filterToReplaceIndex >= 0) {
        // if filter is selected, and the selection is in new filters,
        // it means we receive a subset => no update (negative condition)
        if (
          !(
            this._filters[filterToReplaceIndex].selected &&
            newFilter.items.length &&
            newFilter.items.findIndex(item => item.id === this._filters[filterToReplaceIndex].selected)
          )
        ) {
          this._filters[filterToReplaceIndex] = newFilter;
        }
      } else {
        this._filters.push(newFilter);
      }
    });
  }

  /**
   * Validate if routing filter is coming from save, and if yes,
   * get the full list of routing filters and check if saved id is in the list.
   */
  validateRoutingFilter(taskListConfiguration: TaskListConfiguration) {
    const routingTags = this.getRoutingTagsForTaskListFilters();

    const filters = taskListConfiguration.filters;

    let fitlerToReplace: Filter;
    const filterToReplaceIndex = filters.findIndex(filter => {
      if (filter.type === FilterType.Routing && filter.isSaved) {
        fitlerToReplace = routingTags.find(routingTag => routingTag.type === FilterType.Routing);

        if (fitlerToReplace.items.some(item => item.id === filter.selected)) {
          fitlerToReplace.selected = filter.selected;
        } else {
          // delete in storage because selected id is invalid
          this.deleteSelectedFilter(FilterType.Routing);
        }

        return true;
      }
    });

    if (filterToReplaceIndex >= 0 && fitlerToReplace) {
      filters[filterToReplaceIndex] = fitlerToReplace;
      this._filters = filters;
    }
  }

  /**
   * Update filter list with selected one
   */
  setSelectedFilter(newFilter: Filter) {
    const filterToReplaceIndex = this._filters.findIndex(currentFilter => {
      return newFilter.type === currentFilter.type;
    });

    if (filterToReplaceIndex >= 0) {
      this._filters[filterToReplaceIndex] = newFilter;
    }
  }

  /**
   * Save filter selection in storage.
   *
   * @remarks
   * We don't save 'publication' and 'mention', because with only the saved id, we can't have the full list of filters.
   * (see how it works in updateFilters() comment)
   */
  async saveSelectedFilter(type: FilterType, selected: any) {
    const excluded = [FilterType.Publication, FilterType.Mention];

    if (!excluded.includes(type)) {
      if (selected === null) {
        this.deleteSelectedFilter(type);
      } else {
        let previousFilters = await this.storage.get(this.storageKey);

        if (previousFilters) {
          previousFilters[type] = selected;
        } else {
          previousFilters = { [type]: selected };
        }

        this.storage.set(this.storageKey, previousFilters);
      }
    }
  }

  /**
   * Delete filter from storage
   */
  private async deleteSelectedFilter(type: string) {
    const savedFilters = await this.storage.get(this.storageKey);
    delete savedFilters[type];
    this.storage.set(this.storageKey, savedFilters);
  }

  /**
   * Get filters from storage, and merge it with current filters.
   */
  async getFiltersMergedWithSave(): Promise<Filter[]> {
    const storageFilters = await this.storage.get(this.storageKey);

    const savedFilters: Filter[] = [];

    if (storageFilters) {
      for (const filter in storageFilters) {
        savedFilters.push({
          type: filter as FilterType,
          items: [{ id: storageFilters[filter] }],
          selected: storageFilters[filter],
          isSaved: true,
        });
      }
    }

    if (savedFilters.length) {
      savedFilters.forEach(savedFilter => {
        const filterToReplaceIndex = this._filters.findIndex(currentFilter => currentFilter.type === savedFilter.type);

        if (filterToReplaceIndex >= 0) {
          this._filters[filterToReplaceIndex] = savedFilter;
        } else {
          this._filters.push(savedFilter);
        }
      });
    }

    return this.filters;
  }

  /**
   * Routing tags are for filter 'Team' (Team is the only one routing tag)
   */
  getRoutingTagsForTaskListFilters(): Filter[] {
    const user = this.alcmeonContextService.self;

    if (user.routing_tags) {
      const groupedRoutingTags = {};
      //we need to group them by group name
      user.routing_tags.forEach(routingTag => {
        if (!groupedRoutingTags[routingTag.group.name]) {
          //initialisation
          groupedRoutingTags[routingTag.group.name] = [
            {
              id: null,
              name: this.basicService.translate('all_feminin'),
            },
          ];
          groupedRoutingTags[routingTag.group.name].id = routingTag.group.id; // tempo store the id
        }
        groupedRoutingTags[routingTag.group.name].push(routingTag);
      });

      //Once they are grouped together we output it as an array
      const routingTagsFilters: Filter[] = [];

      for (const name in groupedRoutingTags) {
        const items = groupedRoutingTags[name];

        // for Choice for app-select
        items.forEach((item: TagCompany) => {
          item.label = item.name;
        });

        routingTagsFilters.push({
          name: name,
          items: items,
          type: FilterType.Routing,
          // tag_index: items.length > 1 && items[1].group.tag_index ? items[1].group.tag_index : null, // TODO check if usefull with tags
          selected: null,
          id: items.id,
        } as Filter);

        delete items.id; // delete the tempo id
      }

      return routingTagsFilters;
    } else {
      return [];
    }
  }

  /**
   * Update "publication" ("comments on" in UI) and "mentions" ("answers to the tweet" in UI) filters.
   *
   * @param taskList task list reponse containing filters or not
   * @remarks
   * Filters "publications" and "mentions", are not in "self" object like filter "team" (routing tags) filter,
   * but in the response of getTasks, because they are relative to a channel,
   * and the existence of messages relative to those filters in the response.
   *
   * FYI, "mention" will be present if :
   * - a user post a tweet mentionning a brand (not a comment on publication from the brand)
   * - there is at least 10 answers, with manual mention to the brand, to this initial tweet
   */
  updateContextualFilters(taskList: TaskList) {
    const contextualFilters = [];

    if (taskList.publicationFilter?.length) {
      contextualFilters.push(this.buildContextualFilter(taskList.publicationFilter, FilterType.Publication));
    } else {
      contextualFilters.push(this.buildContextualFilter([], FilterType.Publication));
    }

    if (taskList.mentionFilter?.length) {
      contextualFilters.push(this.buildContextualFilter(taskList.mentionFilter, FilterType.Mention));
    } else {
      contextualFilters.push(this.buildContextualFilter([], FilterType.Mention));
    }

    this.updateFilters(contextualFilters);
  }

  /**
   * Build a filter object dedicated to be manipulated and displayed, for "publication" and "mention" filters.
   *
   * @param filters list of publications or mentions dedicated to build the filter
   * @param type 'publication' or 'mention'
   * @returns filter ready to rock
   */
  private buildContextualFilter(filters: ContextualFilter[], type: FilterType): Filter {
    let items = [];

    if (filters.length) {
      const all = [
        {
          id: null,
          name: this.basicService.translate('all_feminin'),
          label: this.basicService.translate('all_feminin'),
        },
      ];

      const realItems = filters.map(filter => {
        const date = this.dateTimezonePipe.transform(filter.created_time, 'short');

        const contentLength = 25;
        const content =
          filter.content.length > contentLength
            ? `${filter.content.substring(0, contentLength - 1)}\u2026` /* ... */
            : filter.content;

        const name = `${date} ${content} (${filter.count})`;

        return {
          id: filter.id,
          name,
          label: name,
        };
      });

      items = [...all, ...realItems];
    }

    const contextualFitler: Filter = {
      name: this.basicService.translate(`inbox/filters/${type}`),
      type: type,
      items,
      selected: null,
      tooltip: this.basicService.translate('inbox/filters/tooltip'),
    };

    return contextualFitler;
  }
}
